// src/utils/log-manager.ts
// @ts-ignore
// import structuredClone from '@ungap/structured-clone';
import Dexie, { type Table } from 'dexie';

import { req } from '@/req/req';
import { useUserStore } from '@/stores/user.store.ts';

// =============================================================================
// Type definitions
// =============================================================================
export interface LogEntry {
  id?: number;
  level: number; // pino level: 10=trace, 20=debug, 30=info, 40=warn, 50=error, 60=fatal
  time: number; // epoch ms
  msg: string;
  caller?: string; // Optional call stack location
  [key: string]: any;
}

export interface LogManagerConfig {
  batchSize?: number;
  uploadIntervalMs?: number;
  maxRetentionCount?: number;
}

// =============================================================================
// Dexie database
// =============================================================================
class LogDB extends Dexie {
  logs!: Table<LogEntry>;

  constructor() {
    super('tintin:logs');
    this.version(2).stores({
      logs: '++id,level,time,caller',
    });
  }
}
const db = new LogDB();

// =============================================================================
// LogManager singleton (core)
// =============================================================================
class LogManager {
  // -------------------------- Configuration (modifiable at runtime) --------------------------
  private batchSize = 100;
  private uploadIntervalMs = 10_000;
  private maxRetentionCount = 200_000;

  private uploading = false;
  private uploadTimer: number | null = null;

  // -------------------------- Configuration interface --------------------------
  configure(options: LogManagerConfig = {}) {
    if (options.batchSize !== undefined) this.batchSize = options.batchSize;
    if (options.uploadIntervalMs !== undefined) {
      this.uploadIntervalMs = options.uploadIntervalMs;
      this.restartTimer();
    }
    if (options.maxRetentionCount !== undefined) {
      this.maxRetentionCount = options.maxRetentionCount;
    }
  }

  // -------------------------- Add log (single entry point) --------------------------
  async add(log: Omit<LogEntry, 'id'>) {
    try {
      // @ungap/structured-clone is currently the strongest IndexedDB serialization solution
      await db.logs.add(structuredClone(log) as LogEntry);
    } catch (err) {
      // Silently discard when IndexedDB is full or quota exceeded, must not affect business logic
      console.warn('[LogManager] IndexedDB write failed, log discarded', err);
    }
  }

  // -------------------------- Start auto upload --------------------------
  private start() {
    if (this.uploadTimer !== null) return;

    this.uploadTimer = window.setInterval(
      () => this.scheduleUpload(),
      this.uploadIntervalMs,
    );

    // Upload immediately after startup (faster cold start)
    this.scheduleUpload();
  }

  private restartTimer() {
    if (this.uploadTimer !== null) {
      clearInterval(this.uploadTimer);
      this.uploadTimer = null;
    }
    this.start();
  }

  // -------------------------- Smart scheduling upload (non-blocking main thread) --------------------------
  private scheduleUpload() {
    if (this.uploading || !navigator.onLine) return;
    if (!useUserStore.getState().user?.id) return;

    const run = () => void this.uploadBatch();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 5000 });
    } else {
      setTimeout(run, 100);
    }
  }

  // -------------------------- Single batch upload --------------------------
  private async uploadBatch() {
    if (this.uploading) return;
    this.uploading = true;

    try {
      const batch = await db.logs.limit(this.batchSize).toArray();
      if (batch.length === 0) return;

      const payload = batch.map((log) => ({
        eventTime: log.time,
        content: log.msg,
        caller: log.caller,
        // Preserve all original fields (backend can parse itself)
        ...structuredClone(log),
        id: undefined,
        msg: undefined,
        level: log.level,
      }));

      await req.post('/api/logs/batch', payload, {
        timeout: 15_000,
      });

      const ids = batch.map((l) => l.id!).filter(Boolean);
      await db.logs.bulkDelete(ids);

      // Drain mode: if just filled a batch, queue is deep, immediately go again
      if (batch.length === this.batchSize) {
        this.scheduleUpload();
      }
    } catch (err) {
      console.warn(
        '[LogManager] Upload failed, will auto retry next time',
        err,
      );
      // Don't delete local logs on failure, wait for next retry
    } finally {
      this.uploading = false;
    }
  }

  // -------------------------- Force sprint upload before page unload --------------------------
  async flush() {
    if (this.uploadTimer !== null) {
      clearInterval(this.uploadTimer);
      this.uploadTimer = null;
    }

    while (navigator.onLine && (await db.logs.count()) > 0) {
      await this.uploadBatch().catch(() => {});
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  // -------------------------- Periodic cleanup of old logs (prevent IndexedDB explosion) --------------------------
  private startPruneJob() {
    setInterval(
      async () => {
        try {
          const total = await db.logs.count();
          if (total > this.maxRetentionCount) {
            const excess = total - this.maxRetentionCount;
            const keys = await db.logs
              .orderBy('id')
              .limit(excess)
              .primaryKeys();
            await db.logs.bulkDelete(keys);
            console.info(
              `[LogManager] Auto cleaned ${excess} old logs, ${total - excess} remaining`,
            );
          }
        } catch (err) {
          console.error('[LogManager] Cleanup failed', err);
        }
      },
      60 * 60 * 1000,
    ); // Once per hour
  }

  // -------------------------- Constructor auto starts all tasks --------------------------
  constructor() {
    this.start(); // Auto upload polling
    this.startPruneJob(); // Auto cleanup task (called in constructor, completely legal!)
  }
}

// =============================================================================
// Singleton export + global initialization
// =============================================================================
export const logManager = new LogManager();

// Best effort upload before page closes
window.addEventListener('beforeunload', () => logManager.flush());

// Optional: expose manual flush interface (critical business can report immediately)
declare global {
  interface Window {
    __FLUSH_LOGS__?: () => Promise<void>;
  }
}
window.__FLUSH_LOGS__ = () => logManager.flush();
